import { useRef } from "react";
import { Button } from "../ui";
import { RatingStars } from "./RatingStars";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import { ProductNameText } from "../lib/ProductNameText";
import { productDisplayName } from "../lib/productText";
import { useSkuCopyToast } from "../lib/skuCopyToast";
import {
  fallbackProductImage,
  normalizeProductImage,
} from "../lib/fallback-image";
import { navigateExternal } from "../lib/postmessage";
import { recordSummaryViewMore } from "../api/summaryViewMore";
import { useDragScrollbar } from "../lib/useDragScrollbar";
import type { BundleComponent, ProductDetail } from "../types";
import { FeatureGallery } from "./pick/FeatureGallery";
import {
  AddCartPickItem,
  type AddCartProductFields,
} from "./pick/pickAddCartItem";
import {
  PickBadges,
  PickProductPrice,
  type PickBadgeEntry,
} from "./pick/pickShared";
import { cartSkuForProduct } from "./pick/cartSku";

interface Props {
  data: ProductDetail;
  sessionToken: string;
}

// 번들 자식 → 심플 카드 필드 매핑. 각 카드 CTA 는 feature variant(``$SKU$ features summary``).
function _bundleComponentToCard(c: BundleComponent): AddCartProductFields {
  return {
    sku: c.sku,
    feedSku: c.feedSku,
    modelCode: c.model,
    name: c.name,
    image: c.image,
    price: c.price ?? undefined,
  };
}

// 기획 §9·§2-3: 구성품은 가격 내림차순 정렬(원본 불변).
export function sortBundleComponentsByPriceDesc(
  components: BundleComponent[],
): BundleComponent[] {
  return [...components].sort(
    (a, b) => (b.price?.amount ?? 0) - (a.price?.amount ?? 0),
  );
}

/* 기획 §9: 번들 구성품 심플 카드 — 2건 이상이면 compare/되묻기 카드와 동일한
 * 가로 스와이프(useDragScrollbar). SimpleProductCard 와 동일 마크업·스크롤 정책. */
export function BundleComponentsCarousel({
  components,
  bundleSize,
  sessionToken,
}: {
  components: BundleComponent[];
  bundleSize: number;
  sessionToken: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null!);
  const barRef = useRef<HTMLDivElement>(null!);
  const trackRef = useRef<HTMLDivElement>(null!);
  const scrollbarRef = useRef<HTMLDivElement>(null!);
  useDragScrollbar(scrollRef, barRef, trackRef, scrollbarRef, [
    components.length,
  ]);

  const multiple = components.length >= 2;
  let listClass = "pick-list add-cart";
  if (multiple) {
    listClass += " pick-list--carousel is-multiple";
  }
  const bundleTitle = useLabel("productsInBundle", "Products in this Bundle:");

  return (
    <div className="ai-gp1--section pick-bundle">
      <p className="pick-bundle__title">
        {bundleTitle} {bundleSize}
      </p>
      <div
        ref={scrollRef}
        className="ai-gp1-scroll--container"
        style={{ overscrollBehaviorX: "contain", touchAction: "pan-x" }}
      >
        <ul className={listClass}>
          {components.map((c) => (
            <AddCartPickItem
              key={c.sku}
              item={_bundleComponentToCard(c)}
              sessionToken={sessionToken}
              ctaVariant="feature"
            />
          ))}
        </ul>
      </div>
      <div ref={scrollbarRef} className="ai-gp1-custom--scrollbar hidden">
        <div ref={trackRef} className="ai-gp1-scrollbar--track">
          <div ref={barRef} className="ai-gp1-scrollbar--bar" />
        </div>
      </div>
    </div>
  );
}

export default function ProductDetailCard({ data, sessionToken }: Props) {
  const { copySku } = useSkuCopyToast();
  const learnMoreLabel = useLabel("learnMore", "Learn More");
  const addToBasketLabel = useLabel("addToBasket", "Add to Basket");
  const reviewsLabel = useLabel("reviews", "reviews");
  const images = data.images ?? [];
  const heroImage = data.image
    ? normalizeProductImage(data.image)
    : fallbackProductImage();
  const badges = (data.badges ?? []) as PickBadgeEntry[];
  const displayName = productDisplayName(data.name, data.sku);

  if (data.galleryOnly) {
    return (
      <div className="ai-gp1--section">
        <div className="ai-gp1-pick--list">
          <ul className="pick-list">
            <li className="pick-item pick-item--feature">
              <div className="pick-item--inner">
                <FeatureGallery
                  images={images}
                  fallbackImage={heroImage}
                  altName={displayName}
                />
                {data.name ? (
                  <div className="product-inner">
                    <div className="product-ufn pick-feature__title">
                      <ProductNameText name={data.name} sku={data.sku} />
                    </div>
                  </div>
                ) : null}
              </div>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  // 미판매/단종 참고카드(LGCOMMIE-8041): 판매불가(커머스 미온보딩·판매종료) 제품은
  // 미판매/단종(커머스 미온보딩·판매종료)은 **가격 영역 자체를 미노출**한다(라벨 없음·구매 버튼 숨김).
  const notForSale =
    data.salesStatus === "NOT_SOLD" ||
    data.salesStatus === "DISCONTINUED" ||
    data.modelStatus === "DISCONTINUED" ||
    data.inStock === false ||
    data.price == null;

  const handleLearnMore = () => {
    if (data.sku && sessionToken) {
      recordSummaryViewMore(sessionToken, data.sku);
    }
    if (data.pdpUrl) {
      navigateExternal(data.pdpUrl, undefined, { sessionId: sessionToken });
      return;
    }
    void ctaTranslate({
      code: "product_detail.tell_me_more_about",
      params: { name: displayName },
      sessionToken,
      fallback: `Tell me more about ${displayName}`,
    });
  };

  const handleAddToBasket = () => {
    const cartSku = cartSkuForProduct(data);
    void ctaTranslate({
      code: "product_detail.add_name_to_basket",
      params: { name: displayName, sku: data.sku },
      sessionToken,
      fallback: `Add ${displayName} to Basket`,
      toolCall: {
        tool: "commerce_add_to_cart",
        args: { sku: cartSku },
      },
    });
  };

  const handleReviews = () => {
    void ctaTranslate({
      code: "product_detail.sku_reviews",
      params: { name: displayName, sku: data.sku },
      sessionToken,
      fallback: `${data.sku} reviews`,
    });
  };

  const handleSkuCopy = () => {
    copySku(data.sku);
  };

  const bundleComponents =
    data.isBundle && !data.deferBundleComponents && data.bundleComponents?.length
      ? sortBundleComponentsByPriceDesc(data.bundleComponents)
      : [];

  return (
    <>
      <div className="ai-gp1--section">
        <div className="ai-gp1-pick--list">
          <ul className="pick-list">
            <li className="pick-item pick-item--feature">
              <div className="pick-item--inner">
                <FeatureGallery
                  images={images}
                  fallbackImage={heroImage}
                  altName={displayName}
                />
                <div className="product-inner">
                  <div className="product-sku">
                    <button
                      type="button"
                      className="pick-feature__sku-copy button"
                      onClick={handleSkuCopy}
                      title={`Copy SKU: ${data.sku}`}
                    >
                      {data.sku}
                    </button>
                  </div>

                  <div className="product-ufn pick-feature__title">
                    <ProductNameText name={data.name} sku={data.sku} />
                  </div>

                  {typeof data.rating === "number" && data.rating > 0 && (
                    <button
                      type="button"
                      className="pick-feature__rating"
                      onClick={handleReviews}
                    >
                      <RatingStars
                        rating={data.rating}
                        className="pick-feature__rating-stars"
                        size={10}
                      />
                      <span className="pick-feature__rating-num">
                        {data.rating.toFixed(1)}
                      </span>
                      <span className="pick-feature__rating-count">
                        ({data.reviewCount.toLocaleString()} {reviewsLabel})
                      </span>
                    </button>
                  )}

                  {!notForSale && (
                    <PickProductPrice price={data.price ?? undefined} />
                  )}
                </div>

                <div className="pick-btns pick-btns--feature">
                  {(!notForSale || data.pdpUrl) && (
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => handleLearnMore()}
                    >
                      {learnMoreLabel}
                    </Button>
                  )}
                  {!notForSale && (
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => handleAddToBasket()}
                    >
                      {addToBasketLabel}
                    </Button>
                  )}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {bundleComponents.length > 0 && (
        <BundleComponentsCarousel
          components={bundleComponents}
          bundleSize={data.bundleSize ?? bundleComponents.length}
          sessionToken={sessionToken}
        />
      )}
    </>
  );
}
